import { z } from 'zod';
import { Id, IsoDateTime, JsonObject } from './common.ts';
import { Escalation } from './escalation.ts';
import { ObligationChannelKind, ObligationRespondent, ObligationSourceKind } from './obligation.ts';

/**
 * Added by 26a (brief 26 §2, §3.1): the business outcome of a case, separate from its activities. Every case carries
 * a resolution condition fixed at start (`ProcessDefinitionMeta.resolution`, or for `platform.agent-run` the serving
 * agent's `spec.outcome.resolution`), pinned on the instance (`ReleasePin.resolution`). The outcome state
 * (`open | waiting | resolved | abandoned`) is derived from the condition plus open obligations; an activity finishing
 * changes nothing by itself. Resolved only on verified effects, never on an agent's claim.
 *
 * `process.ts`, `agent.ts` and `case.ts` import this file, so it imports none of them; `ResolutionFacts` and
 * `caseDisplayStateFor` live in `case-outcome.ts`.
 */

/** Platform default of `ResolutionSpec.maxContinuations` (and of profile `cases.continuation.maxRuns`). */
export const DEFAULT_MAX_CONTINUATIONS = 3;
/** Upper bound of `ResolutionSpec.maxContinuations`. */
export const MAX_CONTINUATIONS_LIMIT = 10;

/**
 * Reserved owner role (§3.7): resolves to the case's `createdBy` principal (set as `assigneePrincipalId` when the
 * obligation is written). Warn mode writes unowned gaps on `resolution.ownerRole ?? PLATFORM_CASE_CREATOR_ROLE`.
 */
export const PLATFORM_CASE_CREATOR_ROLE = 'platform.case-creator';

/** One effect a `verified_effects` condition needs: a verified receipt of `tool` whose `verifiedEffect ⊇ expect`. */
export const EffectKey = z
  .object({
    tool: z.string().min(1).max(200),
    /** Entity type of the effect's target (e.g. the `EntityRef.type` the receipt wrote), when it matters. */
    targetType: z.string().min(1).max(200).optional(),
    /** Subset of `receipt.verifiedEffect` that must match (deep-equal per key). */
    expect: JsonObject.optional(),
  })
  .meta({ id: 'EffectKey' });
export type EffectKey = z.infer<typeof EffectKey>;

/**
 * When a case counts as resolved: `verified_effects` (receipts with `status: 'succeeded'` and `verifiedEffect`);
 * `predicate` (a pack evaluator registered with `registerResolutionEvaluator`, pure over `ResolutionFacts`);
 * `activity_completed` (explicitly no business outcome: reports, sweeps, eval runs — holds when the last `run#`
 * succeeded).
 */
export const ResolutionCondition = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('verified_effects'),
      effects: z.array(EffectKey).min(1).max(50),
      match: z.enum(['all', 'any']),
    }),
    z.object({
      kind: z.literal('predicate'),
      /** Evaluator name registered by the pack (`<module>.<name>`). */
      evaluator: z.string().min(1).max(200),
      /** Plain-words condition for the card ("the external record is updated and the agreed change is confirmed"). */
      title: z.string().min(1).max(300),
    }),
    z.object({ kind: z.literal('activity_completed') }),
  ])
  .meta({ id: 'ResolutionCondition' });
export type ResolutionCondition = z.infer<typeof ResolutionCondition>;
export type ResolutionConditionKind = ResolutionCondition['kind'];

/**
 * The declared resolution of a process or agent. `ownerRole` owns the `decision_needed` obligation (§4.4); it is
 * required unless the condition is `activity_completed` (`resolutionSpecIssues`, `processMetaIssues`).
 * `maxContinuations` (default 3, max 10) may only lower profile `cases.continuation.maxRuns`.
 */
export const ResolutionSpec = z
  .object({
    condition: ResolutionCondition,
    ownerRole: z.string().min(1).max(200).optional(),
    maxContinuations: z.number().int().nonnegative().max(MAX_CONTINUATIONS_LIMIT).optional(),
  })
  .meta({ id: 'ResolutionSpec' });
export type ResolutionSpec = z.infer<typeof ResolutionSpec>;
export type ResolutionSpecInput = z.input<typeof ResolutionSpec>;

/** Problems in a `ResolutionSpec` that the shape alone cannot express (empty = valid). */
export function resolutionSpecIssues(spec: ResolutionSpec, where = 'resolution'): string[] {
  const issues: string[] = [];
  if (spec.condition.kind !== 'activity_completed' && spec.ownerRole === undefined) {
    issues.push(`${where}: ownerRole is required unless the condition is activity_completed`);
  }
  if (spec.condition.kind === 'verified_effects') {
    const keys = spec.condition.effects.map(
      (e) => `${e.tool}|${e.targetType ?? ''}|${JSON.stringify(e.expect ?? {})}`,
    );
    if (new Set(keys).size !== keys.length) issues.push(`${where}: an effect is listed twice`);
  }
  return issues;
}

/** Continuation runs allowed: `min(profile maxRuns, spec.maxContinuations ?? DEFAULT_MAX_CONTINUATIONS)`. */
export function effectiveMaxContinuations(
  spec: Pick<ResolutionSpec, 'maxContinuations'> | undefined,
  profileMaxRuns: number = DEFAULT_MAX_CONTINUATIONS,
): number {
  return Math.min(profileMaxRuns, spec?.maxContinuations ?? DEFAULT_MAX_CONTINUATIONS);
}

/**
 * The resolution an agent serves with (§4.6): its declared `spec.outcome.resolution`; else `activity_completed` for
 * an A0/A1 agent (reports, analysis, drafts); else `undefined` — an A2+ agent without one is `resolution_undefined`
 * (refused in strict mode, a warning and legacy evaluation in warn mode).
 */
export function agentResolutionSpec(agent: {
  spec: { autonomy: string; outcome?: { resolution?: ResolutionSpec | undefined } | undefined };
}): ResolutionSpec | undefined {
  const declared = agent.spec.outcome?.resolution;
  if (declared !== undefined) return declared;
  if (agent.spec.autonomy === 'A0' || agent.spec.autonomy === 'A1') {
    return { condition: { kind: 'activity_completed' } };
  }
  return undefined;
}

/**
 * Added by 26a phase 4 (§4.6): true when an agent must declare `spec.outcome.resolution` — autonomy A2 or above, at
 * least one capability (it can serve a case run) and no declared resolution. A capability-less workload (e.g. the
 * platform notifier) never serves `platform.agent-run`, so it needs none.
 */
export function agentNeedsResolution(agent: {
  spec: {
    autonomy: string;
    capabilities: readonly unknown[];
    outcome?: { resolution?: ResolutionSpec | undefined } | undefined;
  };
}): boolean {
  return agent.spec.capabilities.length > 0 && agentResolutionSpec(agent) === undefined;
}

/**
 * Added by 26a phase 4 (§4.6): `verified_effects` keys that name a tool outside `tools` (the tools the process's
 * activities, or the agent, may use). `tools` undefined = no tool scope declared: nothing can be checked.
 */
export function resolutionEffectToolIssues(
  spec: ResolutionSpec | undefined,
  tools: readonly string[] | undefined,
  where = 'resolution',
): string[] {
  if (spec?.condition.kind !== 'verified_effects' || tools === undefined) return [];
  const allowed = new Set(tools);
  return spec.condition.effects
    .filter((e) => !allowed.has(e.tool))
    .map((e) => `${where}: verified effect tool ${e.tool} is not a tool its activities may use`);
}

// -----------------------------------------------------------------------------------------------
// Profile keys (§3.7): `modules.config.cases.*`
// -----------------------------------------------------------------------------------------------

/**
 * `cases.outcomeEnforcement`: `warn` (default in base/demo this release) writes an unowned gap on
 * `resolution.ownerRole ?? 'platform.case-creator'` with ledger warning `obligation_unowned`, and registers a
 * definition without resolution with warning `resolution_undefined` (evaluated as legacy). `strict` (test.yaml; the
 * next release flips the others) rejects both.
 */
export const OutcomeEnforcement = z.enum(['warn', 'strict']).meta({ id: 'OutcomeEnforcement' });
export type OutcomeEnforcement = z.infer<typeof OutcomeEnforcement>;

/** Default lead time of the obligation reminder before its deadline (`cases.obligations.reminderLeadHours`). */
export const DEFAULT_REMINDER_LEAD_HOURS = 24;

/** Added by 26b: default of `cases.reopenWindowHours` (close a resolved case at once). */
export const DEFAULT_REOPEN_WINDOW_HOURS = 0;

/** Added by 26a: the outcome keys of profile section `modules.config.cases` (core-cases `CasesConfig` spreads them). */
export const CasesOutcomeConfig = z
  .object({
    outcomeEnforcement: OutcomeEnforcement.default('warn'),
    /** A definition's `maxContinuations` may only lower `maxRuns`. */
    continuation: z
      .object({
        maxRuns: z
          .number()
          .int()
          .nonnegative()
          .max(MAX_CONTINUATIONS_LIMIT)
          .default(DEFAULT_MAX_CONTINUATIONS),
      })
      .prefault({}),
    obligations: z
      .object({
        reminderLeadHours: z
          .number()
          .nonnegative()
          .max(24 * 30)
          .default(DEFAULT_REMINDER_LEAD_HOURS),
      })
      .prefault({}),
    /**
     * Added by 26a phase 4 (§4.6): modules whose definitions without a resolution keep the `warn` registration rule
     * (warning `resolution_undefined`, evaluated as legacy) although `outcomeEnforcement` is `strict`. A transition
     * list for packs written before brief 26: each pack removes itself once it declares its resolutions. `bundles`
     * names the Studio bundle admission (`AgentBundle` events). An entry ending in `*` matches a name prefix
     * (`studio-*`: packs exported from Studio). Empty by default.
     */
    legacyModules: z.array(z.string().min(1).max(200)).default([]),
    /**
     * Added by 26b (brief 26 §5.2, res-07): hours a resolved, outcome-aware case stays `in_progress` before it closes,
     * so a revised answer can still reopen it (a `closed` row never reopens). 0 = close at once (default; demo 0).
     * Legacy cases (no resolution, or `resolution.legacy`) close as before, without a window.
     */
    reopenWindowHours: z
      .number()
      .nonnegative()
      .max(24 * 90)
      .default(DEFAULT_REOPEN_WINDOW_HOURS),
  })
  .meta({ id: 'CasesOutcomeConfig' });
export type CasesOutcomeConfig = z.infer<typeof CasesOutcomeConfig>;

/**
 * Added by 26a phase 4 (§4.6): the registration rule for definitions of `module` — `strict` only when the profile is
 * strict and the module is not in `legacyModules`.
 */
export function registrationEnforcement(
  config: Pick<CasesOutcomeConfig, 'outcomeEnforcement' | 'legacyModules'>,
  module: string,
): OutcomeEnforcement {
  if (config.outcomeEnforcement === 'warn') return 'warn';
  const legacy = config.legacyModules.some((entry) =>
    entry.endsWith('*') ? module.startsWith(entry.slice(0, -1)) : entry === module,
  );
  return legacy ? 'warn' : 'strict';
}

/** Outcome state of a case (§2.1). Distinct from `CaseState` (lifecycle) and from activity states. */
export const ResolutionState = z
  .enum(['open', 'waiting', 'resolved', 'abandoned'])
  .meta({ id: 'ResolutionState' });
export type ResolutionState = z.infer<typeof ResolutionState>;

/** Who abandoned the case and why (§2.5: never automatic; a recorded principal and reason). */
export const ResolutionAbandonment = z
  .object({ by: z.string().min(1), reason: z.string().min(1).max(2000), at: IsoDateTime })
  .meta({ id: 'ResolutionAbandonment' });
export type ResolutionAbandonment = z.infer<typeof ResolutionAbandonment>;

/** The evaluated resolution of one case (written to the case row by the evaluator, §4.3). */
export const Resolution = ResolutionSpec.extend({
  state: ResolutionState,
  evaluatedAt: IsoDateTime,
  holds: z.boolean(),
  blockingObligationIds: z.array(Id).default([]),
  /** Plain words for the card, ≤ 500 characters. */
  explanation: z.string().max(500),
  abandoned: ResolutionAbandonment.optional(),
  /** Instance without a pinned spec: evaluated as "process succeeded" (today's behaviour). */
  legacy: z.literal(true).optional(),
})
  .superRefine((r, ctx) => {
    if (r.state === 'abandoned' && r.abandoned === undefined) {
      ctx.addIssue({ code: 'custom', message: 'abandoned needs by, reason and at', path: ['abandoned'] });
    }
    if (r.state === 'resolved' && !r.holds) {
      ctx.addIssue({ code: 'custom', message: 'resolved needs holds: true', path: ['holds'] });
    }
  })
  .meta({ id: 'Resolution' });
export type Resolution = z.infer<typeof Resolution>;

// -----------------------------------------------------------------------------------------------
// Gap routing (§4.2), declared on `AgentDefinition.spec.outcome`
// -----------------------------------------------------------------------------------------------

/** Relative deadline of a gap route; the absolute `Deadline.at` is computed when the obligation is written. */
export const GapRouteDeadline = z
  .object({
    kind: z.enum(['sla', 'contractual']),
    afterHours: z.number().positive().optional(),
    afterBusinessDays: z.number().int().positive().optional(),
    calendarId: z.string().min(1).optional(),
  })
  .superRefine((d, ctx) => {
    if ((d.afterHours === undefined) === (d.afterBusinessDays === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'set exactly one of afterHours and afterBusinessDays' });
    }
  })
  .meta({ id: 'GapRouteDeadline' });
export type GapRouteDeadline = z.infer<typeof GapRouteDeadline>;

/**
 * Routes a gap to an owner (first matching route wins; no route and no `default` → `obligation_unowned` in strict
 * mode, the warn-mode fallback otherwise).
 */
export const GapRoute = z
  .object({
    match: z.object({
      /** Audience a structured-output gap item declares (`{ text, audience }`). */
      audience: z.string().min(1).max(100).optional(),
      source: ObligationSourceKind.optional(),
      default: z.literal(true).optional(),
    }),
    ownerRole: z.string().min(1).max(200),
    respondent: ObligationRespondent.optional(),
    channel: ObligationChannelKind,
    deadline: GapRouteDeadline,
    escalation: Escalation,
    answerSchemaRef: z.string().min(1).max(300).optional(),
  })
  .meta({ id: 'GapRoute' });
export type GapRoute = z.infer<typeof GapRoute>;

/** `AgentDefinition.spec.outcome` (§3.1, §4.2). */
export const AgentOutcomeSpec = z
  .object({
    resolution: ResolutionSpec.optional(),
    /** Structured-output paths whose items are gaps (strings or `{ text, audience }`), e.g. `missingInformation`. */
    gapsFrom: z.array(z.string().min(1).max(200)).max(20).optional(),
    gapRoutes: z.array(GapRoute).max(50).optional(),
  })
  .meta({ id: 'AgentOutcomeSpec' });
export type AgentOutcomeSpec = z.infer<typeof AgentOutcomeSpec>;

/** First route matching a gap (`audience` and `source` must both match when set), else the first `default` route. */
export function routeForGap(
  routes: readonly GapRoute[] | undefined,
  gap: { audience?: string; source: ObligationSourceKind },
): GapRoute | undefined {
  const list = routes ?? [];
  const specific = list.find(
    (r) =>
      r.match.default !== true &&
      (r.match.audience !== undefined || r.match.source !== undefined) &&
      (r.match.audience === undefined || r.match.audience === gap.audience) &&
      (r.match.source === undefined || r.match.source === gap.source),
  );
  return specific ?? list.find((r) => r.match.default === true);
}

// -----------------------------------------------------------------------------------------------
// Case display state (§3.3): one function, so server and workbench agree
// -----------------------------------------------------------------------------------------------

export const CaseDisplayState = z
  .enum([
    'open',
    'waiting_input',
    'waiting_approval',
    'on_hold',
    'resolved',
    'resolved_with_obligations',
    'abandoned',
    'failed',
    'stopping',
  ])
  .meta({ id: 'CaseDisplayState' });
export type CaseDisplayState = z.infer<typeof CaseDisplayState>;

/** Earliest-due open obligation, shown on list rows as `Case.outcome.waitingOn`. */
export const CaseWaitingOn = z
  .object({
    obligationId: Id,
    title: z.string().min(1).max(300),
    ownerRole: z.string().min(1),
    assigneePrincipalId: z.string().min(1).optional(),
    dueAt: IsoDateTime,
    overdue: z.boolean(),
  })
  .meta({ id: 'CaseWaitingOn' });
export type CaseWaitingOn = z.infer<typeof CaseWaitingOn>;

/** `Case.outcome` (§3.3): list rows need it without a detail call. */
export const CaseOutcome = z
  .object({ state: ResolutionState, display: CaseDisplayState, waitingOn: CaseWaitingOn.optional() })
  .meta({ id: 'CaseOutcome' });
export type CaseOutcome = z.infer<typeof CaseOutcome>;

// -----------------------------------------------------------------------------------------------
// Events (§3.4)
// -----------------------------------------------------------------------------------------------

export const RESOLUTION_EVENT_TYPES = {
  changed: 'case.resolution.changed',
  continuationStarted: 'case.continuation.started',
} as const;

/** `case.resolution.changed`: emitted only on a state change. */
export const CaseResolutionChangedData = z
  .object({
    caseId: Id,
    from: ResolutionState,
    to: ResolutionState,
    holds: z.boolean(),
    blockingObligationIds: z.array(Id).default([]),
    commandId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'CaseResolutionChangedData' });
export type CaseResolutionChangedData = z.infer<typeof CaseResolutionChangedData>;

/** `case.continuation.started`: `run#<round>` was planned because the obligations in `triggeredBy` settled. */
export const CaseContinuationStartedData = z
  .object({
    caseId: Id,
    processInstanceId: Id,
    activationId: z.string().min(1).max(200),
    round: z.number().int().min(2),
    triggeredBy: z.array(Id).default([]),
  })
  .meta({ id: 'CaseContinuationStartedData' });
export type CaseContinuationStartedData = z.infer<typeof CaseContinuationStartedData>;
